import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

export interface PeriodListItem {
  [key: string]: any;
}

export interface GetPeriodListResponse {
  status: boolean;
  body: {
    periodListData: PeriodListItem[];
  };
}

// The period-wise-attendance picker screen (StaffPeriodWiseAttendanceComponent)
// reads periodListData[0] as one object with three buckets, rather than
// treating periodListData itself as a flat list of periods (unlike
// StaffClassAttendanceComponent's own tab-strip, which predates this screen
// and is left as-is here).
export interface PeriodBucketItem {
  classId: number;
  timeTableConfigId: number;
  periodId: number;
  subjectId: number | null;
  staffId: number | null;
  subject: string | null;
  staffImage: string | null;
  staffName: string | null;
  periodStartTime: string;
  periodEndTime: string;
  // 0 | 1, not a real boolean, per the actual API response.
  isAttendanceMarked: 0 | 1;
  periodDuration: string;
  periodStatus: 'Ongoing' | 'Upcoming' | 'Completed';
}

export interface PeriodListBuckets {
  onGoingClassesData: PeriodBucketItem[];
  upcomingClassesData: PeriodBucketItem[];
  completedClassesData: PeriodBucketItem[];
}

export interface GetPeriodListBucketsResponse {
  status: boolean;
  body: {
    periodListData: PeriodListBuckets[];
  };
}

export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'LATE' | 'LEAVE' | null;

export interface AttendanceStudent {
  studentId: number;
  classId: number;
  studentName: string;
  studentImage: string;
  rollNumber: number;
  registrationId: string;
  Attendance: AttendanceStatus;
  pkAttendanceId: string | null;
  leaveInfo: any;
  [key: string]: any;
}

export interface AttendanceGroup {
  group: 'REGULAR' | 'LEAVE_APPROVED' | 'LEAVE_PENDING';
  label: string;
  students: AttendanceStudent[];
}

export interface GetStudentListResponse {
  status: boolean;
  body: {
    groups?: AttendanceGroup[];
    studentList?: AttendanceStudent[];
    allowedToMarkAttendance: boolean;
    attendanceTaken: boolean;
  };
}

export interface MarkAttendanceEntry {
  fkStudentId: number;
  attendance: 'PRESENT' | 'ABSENT' | 'LATE' | 'LEAVE';
  pkAttendanceId: string | null;
}

export interface MarkAttendanceBody {
  classId: string;
  staffId: string;
  attendanceDate: string;
  periodId: string | null;
  lang: string;
  attendance: MarkAttendanceEntry[];
}

export interface MarkAttendanceResponse {
  status: boolean;
  body: {
    message: string;
  };
}

@Injectable({ providedIn: 'root' })
export class StaffAttendanceApiService {
  private url = AppSettings.API_ENDPOINT_STAFF;
  private getPeriodListEndpoint = apiConstants.getStaffPeriodList;
  private getStudentListEndpoint = apiConstants.getStudentListForAttendance;
  private markAttendanceEndpoint = apiConstants.markStaffAttendance;

  constructor(private http: HttpClient) {}

  getPeriodList(staffId: string, classId: string, selectedDate: string) {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('lang', 'en')
      .set('classId', classId)
      .set('attendanceDate', selectedDate);

    return this.http
      .get<GetPeriodListResponse>(`${this.url}${this.getPeriodListEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  // Same endpoint/params as getPeriodList above, typed for the Ongoing/
  // Upcoming/Completed bucketed shape the period-wise-attendance picker
  // screen expects (periodListData[0] holding the three buckets).
  getPeriodListBuckets(staffId: string, classId: string, selectedDate: string) {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('lang', 'en')
      .set('classId', classId)
      .set('attendanceDate', selectedDate);

    return this.http
      .get<GetPeriodListBucketsResponse>(`${this.url}${this.getPeriodListEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  getStudentList(staffId: string, classId: string, date: string, periodId: string | null) {
    let params = new HttpParams()
      .set('staffId', staffId)
      .set('lang', 'en')
      .set('classId', classId)
      .set('attendanceDate', date);
    if (periodId) {
      params = params.set('periodId', periodId);
    }

    return this.http
      .get<GetStudentListResponse>(`${this.url}${this.getStudentListEndpoint}`, { params })
      .pipe(map((res) => res));
  }

  markAttendance(body: MarkAttendanceBody) {
    return this.http
      .post<MarkAttendanceResponse>(`${this.url}${this.markAttendanceEndpoint}`, body)
      .pipe(map((res) => res));
  }

  // Merges LEAVE_APPROVED (defaulted to LEAVE) + LEAVE_PENDING + REGULAR
  // groups into one flat list, falling back to studentList when groups is
  // absent. Mirrors MarkAttendance.js:263-290 exactly.
  mergeStudentGroups(body: GetStudentListResponse['body']): {
    studentList: AttendanceStudent[];
    studentsOnLeaveIds: Set<number>;
    pendingLeaveIds: Set<number>;
  } {
    const studentsOnLeaveIds = new Set<number>();
    const pendingLeaveIds = new Set<number>();

    if (!body.groups) {
      return { studentList: body.studentList || [], studentsOnLeaveIds, pendingLeaveIds };
    }

    const leaveApproved = body.groups.find(g => g.group === 'LEAVE_APPROVED');
    const leavePending = body.groups.find(g => g.group === 'LEAVE_PENDING');
    const regular = body.groups.find(g => g.group === 'REGULAR');

    const merged: AttendanceStudent[] = [];

    if (leaveApproved) {
      leaveApproved.students.forEach(s => {
        studentsOnLeaveIds.add(s.studentId);
        // Only default to LEAVE if the server didn't already send a real
        // status — e.g. attendance may have been taken before the leave
        // was approved, and that existing mark shouldn't be discarded.
        merged.push({ ...s, Attendance: s.Attendance || 'LEAVE' });
      });
    }
    if (leavePending) {
      leavePending.students.forEach(s => {
        pendingLeaveIds.add(s.studentId);
        merged.push(s);
      });
    }
    if (regular) {
      merged.push(...regular.students);
    }

    return { studentList: merged, studentsOnLeaveIds, pendingLeaveIds };
  }
}
